// src/components/BannerSlider.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { getImageUrl } from '../utils/getImageUrl';
import ProductSlider from './ProductSlider';
import CategoryBanner, { visibleCategoryTiles } from './CategoryBanner';

const FALLBACK_IMAGE = '/static/images/no-image.svg';

// Font size mapping for text slides
const FONT_SIZE_MAP = {
  'xs': 'text-xs',
  'sm': 'text-sm',
  'base': 'text-base',
  'lg': 'text-lg',
  'xl': 'text-xl',
  '2xl': 'text-2xl',
  '3xl': 'text-3xl',
  '4xl': 'text-4xl',
  '5xl': 'text-5xl',
  '6xl': 'text-6xl',
  '7xl': 'text-7xl',
  '8xl': 'text-8xl',
  '9xl': 'text-9xl'
};

const FONT_WEIGHT_MAP = {
  'normal': 'font-normal',
  'medium': 'font-medium',
  'semibold': 'font-semibold',
  'bold': 'font-bold'
};

const TEXT_ALIGN_MAP = {
  'left': 'text-left',
  'center': 'text-center',
  'right': 'text-right'
};

const VERTICAL_ALIGN_MAP = {
  'top': 'justify-start',
  'center': 'justify-center',
  'bottom': 'justify-end'
};

/**
 * TextSlideContent - Renders the text content for a text slide
 * Groups rows into lines based on widths (each line <= 100% total).
 * Lines stack vertically, items within a line are side by side.
 * Each line gets equal height share.
 */
const TextSlideContent = ({ slide, onLinkClick }) => {
  const textRows = slide.text_rows || [];

  const handleTextClick = (textRow, e) => {
    if (textRow.link_url) {
      e.stopPropagation();
      onLinkClick(textRow.link_url, textRow.link_target);
    }
  };

  // Use slide-level alignment as default, but rows can override
  const slideTextAlign = slide.text_align || 'center';
  const slideVerticalAlign = slide.vertical_align || 'center';

  // Group rows into lines where each line's total width <= 100
  const lines = [];
  let currentLine = [];
  let currentLineWidth = 0;

  textRows.forEach((row) => {
    const rowWidth = row.width || 100;
    if (currentLineWidth + rowWidth <= 100) {
      currentLine.push(row);
      currentLineWidth += rowWidth;
    } else {
      if (currentLine.length > 0) {
        lines.push(currentLine);
      }
      currentLine = [row];
      currentLineWidth = rowWidth;
    }
  });
  if (currentLine.length > 0) {
    lines.push(currentLine);
  }

  return (
    <div
      className="flex flex-col w-full h-full"
      style={{
        paddingLeft: `${slide.padding_x || 16}px`,
        paddingRight: `${slide.padding_x || 16}px`,
        paddingTop: `${slide.padding_y || 12}px`,
        paddingBottom: `${slide.padding_y || 12}px`
      }}
    >
      {lines.map((lineRows, lineIdx) => {
        const isSingleItem = lineRows.length === 1;
        const lineWidth = lineRows.reduce((sum, r) => sum + (r.width || 100), 0);

        return (
          <div
            key={lineIdx}
            className="flex flex-row w-full"
            style={{ flexGrow: 1, flexBasis: 0 }}
          >
            {lineRows.map((row, idx) => {
              const rowTextAlign = row.text_align || slideTextAlign;
              const rowVerticalAlign = row.vertical_align || slideVerticalAlign;
              const rowWidth = row.width || 100;
              const containerAlign = rowWidth < 100 ? (row.container_align || 'center') : 'center';
              const textAlignClass = TEXT_ALIGN_MAP[rowTextAlign] || TEXT_ALIGN_MAP.center;
              const verticalAlignClass = VERTICAL_ALIGN_MAP[rowVerticalAlign] || VERTICAL_ALIGN_MAP.center;

              // For single item in line with width < 100, position it horizontally
              const justifyClass = isSingleItem && rowWidth < 100 ? {
                'left': 'justify-start',
                'center': 'justify-center',
                'right': 'justify-end'
              }[containerAlign] || 'justify-center' : '';

              return (
                <div
                  key={row.id || idx}
                  className={`flex flex-col ${verticalAlignClass} ${justifyClass} ${textAlignClass} ${FONT_SIZE_MAP[row.font_size] || FONT_SIZE_MAP.base} ${FONT_WEIGHT_MAP[row.font_weight] || FONT_WEIGHT_MAP.normal} ${row.link_url ? 'cursor-pointer hover:underline' : ''}`}
                  style={{
                    width: isSingleItem ? '100%' : `${(rowWidth / lineWidth) * 100}%`,
                    color: row.text_color || '#FFFFFF'
                  }}
                  onClick={(e) => handleTextClick(row, e)}
                >
                  <div style={{ width: isSingleItem ? `${rowWidth}%` : '100%' }}>
                    {row.text}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

/**
 * SingleBannerSlider - Displays slides from a SINGLE banner as a slider
 * Supports both image slides and text slides mixed together
 */
const SingleBannerSlider = ({
  banner,
  locationSettings = null,
  autoPlay = true,
  interval = 5000,
  isMobile = false,
  className = ''
}) => {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [imageError, setImageError] = useState({});
  const [imageRatios, setImageRatios] = useState({});

  // Combine images and text slides into a unified slides array
  const slides = useMemo(() => {
    const allSlides = [];

    // Add images as slides (type: 'image')
    if (banner.images && banner.images.length > 0) {
      banner.images.forEach(img => {
        allSlides.push({
          type: 'image',
          id: `img-${img.id}`,
          position: img.position ?? 0,
          image_url: img.image_url,
          thumbnail_url: img.thumbnail_url,
          link_url: img.link_url || banner.link_url || '',
          link_target: img.link_target || banner.link_target || '_self',
          alt_text: img.alt_text || banner.title,
          bannerId: banner.id,
          bannerTitle: banner.title,
          bannerDescription: banner.description
        });
      });
    } else if (banner.desktop_image_url) {
      // Legacy single image support
      allSlides.push({
        type: 'image',
        id: `legacy-${banner.id}`,
        position: 0,
        image_url: banner.desktop_image_url,
        link_url: banner.link_url,
        link_target: banner.link_target || '_self',
        alt_text: banner.title,
        bannerId: banner.id,
        bannerTitle: banner.title,
        bannerDescription: banner.description
      });
    }

    // Add text slides (type: 'text' or 'overlay')
    if (banner.text_slides && banner.text_slides.length > 0) {
      banner.text_slides.forEach(textSlide => {
        if (textSlide.slide_type === 'text') {
          // Text-only slide with background color
          allSlides.push({
            type: 'text',
            id: `text-${textSlide.id}`,
            position: textSlide.position ?? 999,
            ...textSlide
          });
        } else if (textSlide.slide_type === 'overlay') {
          // Text overlay - find the associated image and add overlay to it
          // We'll handle overlay separately when rendering images
          const imageSlide = allSlides.find(s =>
            s.type === 'image' && s.id === `img-${textSlide.image_association}`
          );
          if (imageSlide) {
            imageSlide.textOverlay = textSlide;
          }
        }
      });
    }

    // Sort by position
    allSlides.sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

    return allSlides;
  }, [banner]);

  const nextSlide = useCallback(() => {
    if (slides.length <= 1) return;
    setCurrentIndex((prevIndex) =>
      prevIndex === slides.length - 1 ? 0 : prevIndex + 1
    );
  }, [slides.length]);

  const prevSlide = useCallback(() => {
    if (slides.length <= 1) return;
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? slides.length - 1 : prevIndex - 1
    );
  }, [slides.length]);

  useEffect(() => {
    if (!autoPlay || slides.length <= 1 || isPaused) return;
    const timer = setInterval(nextSlide, interval);
    return () => clearInterval(timer);
  }, [autoPlay, interval, nextSlide, slides.length, isPaused]);

  useEffect(() => {
    if (currentIndex >= slides.length) {
      setCurrentIndex(0);
    }
  }, [slides.length, currentIndex]);

  if (!slides || slides.length === 0) return null;

  const isInternalLink = (url) => {
    if (!url) return false;
    return url.startsWith('/') || (!url.startsWith('http://') && !url.startsWith('https://'));
  };

  const handleLinkClick = (url, target = '_self') => {
    if (!url) return;
    if (isInternalLink(url)) {
      navigate(url);
    } else {
      window.open(url, target);
    }
  };

  const handleSlideClick = (slide) => {
    if (slide.link_url) {
      handleLinkClick(slide.link_url, slide.link_target);
    }
  };

  const getImageSrc = (slide) => {
    return getImageUrl(slide.image_url || slide.thumbnail_url);
  };

  const handleImageError = (slideId) => {
    setImageError(prev => ({ ...prev, [slideId]: true }));
  };

  const handleImageLoad = (slideId, e) => {
    const { naturalWidth, naturalHeight } = e.target;
    if (!naturalWidth || !naturalHeight) return;
    setImageRatios(prev => (prev[slideId] ? prev : { ...prev, [slideId]: naturalWidth / naturalHeight }));
  };

  const configuredHeight = locationSettings
    ? (isMobile ? locationSettings.height_mobile : locationSettings.height)
    : (isMobile ? 150 : 200);

  const autoRatio = slides
    .filter(s => s.type === 'image')
    .map(s => imageRatios[s.id])
    .find(Boolean) || 16 / 5;

  const containerStyle = {
    ...(configuredHeight ? { height: `${configuredHeight}px` } : { aspectRatio: `${autoRatio}` }),
    maxWidth: locationSettings?.max_width ? `${locationSettings.max_width}px` : '100%'
  };

  return (
    <div
      className={`relative w-full rounded-lg overflow-hidden shadow-lg ${className}`}
      style={locationSettings?.max_width ? { maxWidth: `${locationSettings.max_width}px`, margin: '0 auto' } : {}}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className="relative" style={containerStyle}>
        {slides.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-500 ease-in-out ${
              index === currentIndex ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'
            }`}
          >
            {slide.type === 'image' ? (
              // Image slide (with optional text overlay)
              <>
                <img
                  src={imageError[slide.id] ? FALLBACK_IMAGE : getImageSrc(slide)}
                  alt={slide.alt_text || slide.bannerTitle || 'Banner'}
                  className={`w-full h-full object-cover ${slide.link_url ? 'cursor-pointer' : ''}`}
                  style={{
                    objectPosition: slide.textOverlay
                      ? `${slide.textOverlay.bg_horizontal || 'center'} ${slide.textOverlay.bg_vertical || 'center'}`
                      : 'center center'
                  }}
                  onClick={() => handleSlideClick(slide)}
                  onLoad={(e) => handleImageLoad(slide.id, e)}
                  onError={() => handleImageError(slide.id)}
                />

                {/* Text overlay on image */}
                {slide.textOverlay && (
                  <div
                    className="absolute inset-0"
                    onClick={() => slide.textOverlay.link_url && handleLinkClick(slide.textOverlay.link_url, slide.textOverlay.link_target)}
                    style={{ cursor: slide.textOverlay.link_url ? 'pointer' : 'default' }}
                  >
                    <TextSlideContent
                      slide={slide.textOverlay}
                      onLinkClick={handleLinkClick}
                    />
                  </div>
                )}

                {/* Default description overlay (if no text overlay and has description) */}
                {!slide.textOverlay && slide.bannerDescription && (
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                    <h3 className="text-white font-semibold text-base sm:text-lg truncate">
                      {slide.bannerTitle}
                    </h3>
                    <p className="text-white/90 text-xs sm:text-sm line-clamp-2">
                      {slide.bannerDescription}
                    </p>
                  </div>
                )}
              </>
            ) : (
              // Text-only slide with background color
              <div
                className={`w-full h-full ${slide.link_url ? 'cursor-pointer' : ''}`}
                style={{ backgroundColor: slide.background_color || '#3B82F6' }}
                onClick={() => handleSlideClick(slide)}
              >
                <TextSlideContent
                  slide={slide}
                  onLinkClick={handleLinkClick}
                />
              </div>
            )}
          </div>
        ))}
      </div>

      {slides.length > 1 && (
        <>
          <button
            onClick={prevSlide}
            className="absolute left-1 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 rounded-full p-1 transition-all z-20 focus:outline-none"
            aria-label="Previous slide"
          >
            <FaChevronLeft className="w-3 h-3 text-white" />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-1 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 rounded-full p-1 transition-all z-20 focus:outline-none"
            aria-label="Next slide"
          >
            <FaChevronRight className="w-3 h-3 text-white" />
          </button>

          <div className="absolute bottom-1 left-1/2 -translate-x-1/2 flex gap-1 z-20">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-1.5 h-1.5 rounded-full transition-all focus:outline-none ${
                  index === currentIndex
                    ? 'bg-white'
                    : 'bg-white/50 hover:bg-white/75'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </>
      )}

      {isPaused && slides.length > 1 && (
        <div className="absolute top-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded z-20">
          Paused
        </div>
      )}
    </div>
  );
};

/**
 * BannerSlider - Displays MULTIPLE banners
 * For catalog_top on desktop: groups banners by display_width (side by side if widths fit)
 * For sidebars or mobile: stacks vertically
 */
const BannerSlider = ({
  banners = [],
  locationSettings = null,
  autoPlay = true,
  interval = 5000,
  showOnMobile = true,
  isMobile = false,
  className = '',
  location = null,
  activeCategoryId = '',
  onCategorySelect = null,
  categoryCounts = null
}) => {
  // Filter and sort banners based on visibility settings and position
  const visibleBanners = useMemo(() => {
    return banners
      .filter(banner => {
        if (isMobile && !banner.show_on_mobile) return false;
        if (!isMobile && banner.show_on_desktop === false) return false;
        if (banner.banner_type === 'products') {
          return (banner.slider_products || []).length > 0;
        }
        if (banner.banner_type === 'categories') {
          return visibleCategoryTiles(banner, activeCategoryId, categoryCounts).length > 0;
        }
        const hasImages = (banner.images && banner.images.length > 0) || banner.desktop_image_url;
        const hasTextSlides = banner.text_slides && banner.text_slides.some(s => s.slide_type === 'text');
        return hasImages || hasTextSlides;
      })
      .sort((a, b) => (a.position || 0) - (b.position || 0));
  }, [banners, isMobile, activeCategoryId, categoryCounts]);

  // Group banners into lines based on display_width (only for catalog_top on desktop)
  const bannerLines = useMemo(() => {
    const isCatalogDesktop = location === 'catalog_top' && !isMobile;

    if (!isCatalogDesktop) {
      // Stack all banners vertically
      return visibleBanners.map(b => [b]);
    }

    // Group by width like text rows
    const lines = [];
    let currentLine = [];
    let currentLineWidth = 0;

    visibleBanners.forEach((banner) => {
      const bannerWidth = banner.display_width || 100;
      if (currentLineWidth + bannerWidth <= 100) {
        currentLine.push(banner);
        currentLineWidth += bannerWidth;
      } else {
        if (currentLine.length > 0) {
          lines.push(currentLine);
        }
        currentLine = [banner];
        currentLineWidth = bannerWidth;
      }
    });
    if (currentLine.length > 0) {
      lines.push(currentLine);
    }

    return lines;
  }, [visibleBanners, location, isMobile]);

  if (!visibleBanners || visibleBanners.length === 0) return null;
  if (isMobile && !showOnMobile) return null;

  return (
    <div className={`space-y-3 ${className}`}>
      {bannerLines.map((lineBanners, lineIdx) => {
        const isSingleBanner = lineBanners.length === 1;
        const lineWidth = lineBanners.reduce((sum, b) => sum + (b.display_width || 100), 0);

        return (
          <div key={lineIdx} className="flex flex-row gap-3">
            {lineBanners.map(banner => (
              <div
                key={banner.id}
                style={{
                  width: isSingleBanner ? '100%' : `${((banner.display_width || 100) / lineWidth) * 100}%`
                }}
              >
                {banner.banner_type === 'products' ? (
                  <ProductSlider
                    banner={banner}
                    autoPlay={autoPlay}
                    interval={interval}
                    isMobile={isMobile}
                  />
                ) : banner.banner_type === 'categories' ? (
                  <CategoryBanner
                    banner={banner}
                    isMobile={isMobile}
                    activeCategoryId={activeCategoryId}
                    onCategorySelect={onCategorySelect}
                    categoryCounts={categoryCounts}
                  />
                ) : (
                  <SingleBannerSlider
                    banner={banner}
                    locationSettings={locationSettings}
                    autoPlay={autoPlay}
                    interval={interval}
                    isMobile={isMobile}
                  />
                )}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
};

export default BannerSlider;
